import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { brazilianStates } from "@/data/brazilianStates";
import { ReloadIcon } from "@radix-ui/react-icons";
import { DollarSign, Filter, FileCheck } from "lucide-react";
import { useCallback } from "react";
interface DataTableFiltersProps {
    filterState: any;
    updateFilter: (filter: any) => void;
    resetFilters: () => void;
    refetch?: () => void;
}

export function DataTableFilters({ filterState, updateFilter, resetFilters, refetch }: DataTableFiltersProps) {
    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        updateFilter({ key: "search", value: event.target.value });
    };

    const debouncedHandleInputChange = useCallback(
        handleInputChange,
        []
    );

    const isPayedValue = filterState.filters?.is_payed ?? '';

    const hasGeneratedGNRE = filterState.filters?.generated_gnre ?? '';

    const hasNcmAssigned = filterState.filters?.has_ncm_assigned ?? '';

    const handleIsPayedChange = (value: '' | 'true' | 'false') => {
        updateFilter({ key: "is_payed", value });
    };

    const handleHasGNRE = (value: '' | 'true' | 'false') => {
        updateFilter({ key: "generated_gnre", value });
    };

    const handleHasNcmAssigned = (value: '' | 'true' | 'false') => {
        updateFilter({ key: "has_ncm_assigned", value });
    };

    return (
        <div className="flex flex-1 items-center justify-between space-x-2">
            <div className="flex flex-wrap gap-2 items-end">
                <div className="flex flex-col">
                    <label htmlFor="state-filter" className="text-sm font-medium text-gray-700">
                        Pesquisar
                    </label>
                    <Input
                        placeholder="Filtrar..."
                        onChange={debouncedHandleInputChange}
                        className="h-8 py-2 w-[150px] lg:w-[250px]"
                    />
                </div>
                <div className="flex flex-col">
                    <label htmlFor="state-filter" className="text-sm font-medium text-gray-700">
                        Filtrar por Estado
                    </label>
                    <Select
                        value={filterState.filters.state || "all"}
                        onValueChange={(value) => {
                            updateFilter({ key: "state", value: value === "all" ? undefined : value });
                        }}
                    >
                        <SelectTrigger className="w-[140px] px-4 h-8 border rounded-lg text-gray-700 shadow-sm">
                            <SelectValue placeholder="Selecione um Estado" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Todos</SelectItem>
                            {brazilianStates.map((state) => (
                                <SelectItem key={state.value} value={state.value}>
                                    {state.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="icon" className="h-8 w-8">
                            <Filter className="h-4 w-4" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-64 p-4" align="end">
                        <div className="space-y-4">
                            {/* Seção: Status de Pagamento */}
                            <div>
                                <h4 className="font-medium text-sm text-foreground mb-3 flex items-center gap-2">
                                    <DollarSign className="h-4 w-4" />
                                    Status de Pagamento
                                </h4>
                                <div className="space-y-2">
                                    <div className="flex items-center space-x-2">
                                        <Checkbox
                                            id="onlyPaidAll"
                                            checked={isPayedValue === ''}
                                            onCheckedChange={() => handleIsPayedChange('')}
                                        />
                                        <label htmlFor="onlyPaidAll" className="text-sm text-foreground">
                                            Todos
                                        </label>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <Checkbox
                                            id="onlyPaidTrue"
                                            checked={isPayedValue === 'true'}
                                            onCheckedChange={() => handleIsPayedChange('true')}
                                        />
                                        <label htmlFor="onlyPaidTrue" className="text-sm text-foreground">
                                            Somente itens pagos
                                        </label>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <Checkbox
                                            id="onlyPaidFalse"
                                            checked={isPayedValue === 'false'}
                                            onCheckedChange={() => handleIsPayedChange('false')}
                                        />
                                        <label htmlFor="onlyPaidFalse" className="text-sm text-foreground">
                                            Somente itens não pagos
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <DropdownMenuSeparator />

                            <div>
                                <h4 className="font-medium text-sm text-foreground mb-3 flex items-center gap-2">
                                    <DollarSign className="h-4 w-4" />
                                    GNRE Gerado
                                </h4>
                                <div className="space-y-2">
                                    <div className="flex items-center space-x-2">
                                        <Checkbox
                                            id="gnreAll"
                                            checked={hasGeneratedGNRE === ''}
                                            onCheckedChange={() => handleHasGNRE('')}
                                        />
                                        <label htmlFor="gnreAll" className="text-sm text-foreground">
                                            Todos
                                        </label>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <Checkbox
                                            id="gnreTrue"
                                            checked={hasGeneratedGNRE === 'true'}
                                            onCheckedChange={() => handleHasGNRE('true')}
                                        />
                                        <label htmlFor="gnreTrue" className="text-sm text-foreground">
                                            GNRE Gerados
                                        </label>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <Checkbox
                                            id="gnreFalse"
                                            checked={hasGeneratedGNRE === 'false'}
                                            onCheckedChange={() => handleHasGNRE('false')}
                                        />
                                        <label htmlFor="gnreFalse" className="text-sm text-foreground">
                                            GNRE não Gerados
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <DropdownMenuSeparator />

                            {/* Seção: Apto para GNRE (NCM atribuído) */}
                            <div>
                                <h4 className="font-medium text-sm text-foreground mb-3 flex items-center gap-2">
                                    <FileCheck className="h-4 w-4" />
                                    Apto para GNRE
                                </h4>
                                <div className="space-y-2">
                                    <div className="flex items-center space-x-2">
                                        <Checkbox
                                            id="ncmAll"
                                            checked={hasNcmAssigned === ''}
                                            onCheckedChange={() => handleHasNcmAssigned('')}
                                        />
                                        <label htmlFor="ncmAll" className="text-sm text-foreground">
                                            Todos
                                        </label>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <Checkbox
                                            id="ncmTrue"
                                            checked={hasNcmAssigned === 'true'}
                                            onCheckedChange={() => handleHasNcmAssigned('true')}
                                        />
                                        <label htmlFor="ncmTrue" className="text-sm text-foreground">
                                            Com NCM atribuído
                                        </label>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <Checkbox
                                            id="ncmFalse"
                                            checked={hasNcmAssigned === 'false'}
                                            onCheckedChange={() => handleHasNcmAssigned('false')}
                                        />
                                        <label htmlFor="ncmFalse" className="text-sm text-foreground">
                                            Sem NCM atribuído
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </DropdownMenuContent>
                </DropdownMenu>
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => refetch && refetch()}>
                    <ReloadIcon className="h-4 w-4" />
                </Button>
                <Button
                    className="h-8 py-1 text-xs"
                    variant={"outline"}
                    onClick={() => {
                        resetFilters();
                        refetch && refetch();
                    }}
                >
                    Limpar Filtros
                </Button>
            </div>

        </div>
    );
}
